import { observer } from "mobx-react";
import { isMobileView } from "@/utils/screen";
import { MTextInput } from "@/components-v2/TextInput";
import { Box, Container, VBox } from "@/components-v2/Box";
import { useTranslation } from "@/stores/RootStoreContext";
import { MSelect } from "@/components-v2/Select";
import { DataTable, TableColumn } from "@/components-v2/DataTable";
import { Checkbox } from "@/components-v2/Checkbox";
import {
  companyRolesCompanies,
  companyRolesCountryOfResidence,
  companyRolesEmail,
  companyRolesNationality,
  companyRolesRoleType,
  companyRolesValidFrom,
  CompanyRolesViewProps,
  filterCompanyRoleRows,
} from "./companyRolesDomain";
import { AggregatedContactItem } from "./contactsDomain";
import { TextWithTooltip } from "@/components-v2/TextWithTooltip";
import { Icon } from "@/components-v2/Icon.tsx";
import { LastLogin } from "@/common/commonComponents.tsx";

export const CompanyRolesView = observer((props: CompanyRolesViewProps) => {
  const { t } = useTranslation();
  const columns: TableColumn<AggregatedContactItem>[] = [
    {
      name: "name",
      label: t("name"),
      align: "left",
      getValue: (row) => (
        <Box alignItems={"center"} gap={"md"}>
          <Icon size={"sm"} color={"subtle"} name={"user"} />
          <VBox gap={false}>
            <div>{row.name}</div>
            <div className="subtle text-sm">
              <TextWithTooltip {...companyRolesRoleType(row.roleItems)} />
            </div>
          </VBox>
        </Box>
      ),
      getSortValue: (row) => row.name ?? t(companyRolesRoleType(row.roleItems).text),
    },
    {
      name: "companies",
      label: t("portfolioContacts.companies"),
      align: "left",
      getValue: (row) => <TextWithTooltip {...companyRolesCompanies(row.roleItems)} />,
      getSortValue: (row) => t(companyRolesCompanies(row.roleItems).text),
    },
    {
      name: "validFrom",
      label: { key: "company-roles.period.from" },
      align: "left",
      getValue: (row, _, { lang }) => (
        <TextWithTooltip {...companyRolesValidFrom(row.roleItems, lang)} />
      ),
    },
    {
      name: "nationality",
      label: { key: "shareholders.detailed.nationality" },
      align: "left",
      getValue: (row) => <TextWithTooltip {...companyRolesNationality(row.roleItems)} />,
      getSortValue: (row) => t(companyRolesNationality(row.roleItems).text),
    },
    {
      name: "countryOfResidence",
      label: { key: "contacts.company-roles.country" },
      align: "left",
      getValue: (row) => <TextWithTooltip {...companyRolesCountryOfResidence(row.roleItems)} />,
      getSortValue: (row) => t(companyRolesCountryOfResidence(row.roleItems).text),
    },
    {
      name: "email",
      label: t("email"),
      align: "left",
      getValue: (row) => <TextWithTooltip {...companyRolesEmail(row.roleItems)} />,
      getSortValue: (row) => t(companyRolesEmail(row.roleItems).text),
    },
    {
      name: "lastLogin",
      label: { key: "last-login" },
      align: "left",
      getValue: (row) => (
        <LastLogin
          lastLogin={row.roleItems.find((f) => f.role.lastLogin)?.role.lastLogin}
          neverLabel={{ key: "never-logged-in.short" }}
        />
      ),
    },
  ];
  return (
    <Container padding maxWidth="xl">
      <Box vertical={isMobileView()} className={"self-start"} alignItems={"center"}>
        {props.roleNameFilter && (
          <MTextInput
            model={props.roleNameFilter}
            icon={"magnifying-glass"}
            placeholder={t("portfolioContacts.nameFilterPlaceholder")}
            type={"search"}
          />
        )}
        {props.selectedCompany && <MSelect model={props.selectedCompany} />}
        {props.selectedRoleType && <MSelect model={props.selectedRoleType} />}
        <Box marginLeft={isMobileView() ? undefined : "lg"}>
          <Checkbox
            checked={props.filterMissingEmailModel?.value}
            label={props.filterMissingEmailLabel}
            disabled={props.isFilterMissingEmailDisabled}
            onChange={(e) => props.filterMissingEmailModel?.setValue(e.target.checked)}
          />
        </Box>
      </Box>
      <DataTable
        variant={"LARGE"}
        tableColumns={columns}
        tableData={filterCompanyRoleRows(
          props.selectedCompany?.value,
          props.selectedRoleType?.value,
          props.aggregatedPersonItems,
          props.roleNameFilter?.inputValue,
          props.filterMissingEmailModel?.value,
        )}
        getId={(row) => row.aggregatedContactId}
        initialSortBy={"name"}
        initialSortDirection={"asc"}
        onRowClick={(row) => props.navigateToDetails(row.aggregatedContactId)}
        rowActions={props.rowActions}
      />
    </Container>
  );
});
